/**
 * Footer — Cinematic editorial brand footer.
 * Pattern: massive Zertivo wordmark + manifesto line, then
 * 4-col link sitemap, base-bar with legal + social.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, MapPin } from 'lucide-react';

const ease = [0.22, 1, 0.36, 1]; // eslint-disable-line no-unused-vars

const COLS = [
  {
    title: 'Plattform',
    links: [
      { label: 'Verzeichnis', to: '/directory' },
      { label: 'Methodik', to: '/methodology' },
      { label: 'Preise', to: '/pricing' },
      { label: 'Tools', to: '/tools' },
      { label: 'API', to: '/api-docs' },
    ],
  },
  {
    title: 'Branchen',
    links: [
      { label: 'Restaurants', to: '/for/restaurants' },
      { label: 'Handwerk', to: '/for/handwerk' },
      { label: 'Agenturen', to: '/for/agencies' },
      { label: 'Mittelstand', to: '/for/mittelstand' },
      { label: 'Alle Branchen', to: '/industries' },
    ],
  },
  {
    title: 'Unternehmen',
    links: [
      { label: 'Über Zertivo', to: '/about' },
      { label: 'Karriere', to: '/careers' },
      { label: 'Press', to: '/press' },
      { label: 'Investoren', to: '/investors' },
      { label: 'Kontakt', to: '/contact' },
    ],
  },
  {
    title: 'Wissen',
    links: [
      { label: 'Blog', to: '/blog' },
      { label: 'Erfolgsgeschichten', to: '/success-stories' },
      { label: 'FAQ', to: '/faq' },
      { label: 'Status', to: '/status' },
      { label: 'Docs', to: '/docs' },
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-[#0A0F0C] text-white overflow-hidden" data-testid="site-footer">
      {/* ambient glow */}
      <div aria-hidden className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-[#00D387] opacity-[0.06] blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#0F8A5F] opacity-[0.05] blur-3xl pointer-events-none" />

      {/* ─── BRAND BLOCK ─── */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: massive wordmark + manifesto */}
          <div className="lg:col-span-6">
            <Link to="/" className="inline-flex items-baseline group">
              <span className="font-manrope text-[64px] sm:text-[88px] md:text-[112px] lg:text-[128px] font-extralight tracking-[-0.045em] leading-[0.85]">
                Zertivo
              </span>
              <span className="ml-1 inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#00D387] group-hover:scale-110 transition-transform" />
            </Link>
            <p className="mt-8 text-[15px] sm:text-[17px] font-light leading-[1.7] text-white/70 max-w-md">
              Die unabhängige Vertrauens-Infrastruktur für die nächste Generation europäischer Unternehmen.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
              <Link to="/apply" data-testid="footer-cta-apply" className="group inline-flex items-center gap-2 text-white text-[14px] font-light tracking-wide border-b border-white/45 hover:border-white pb-1 transition-colors">
                Prüfung beantragen
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/contact" className="group inline-flex items-center gap-2 text-white/65 hover:text-white text-[14px] font-light tracking-wide transition-colors">
                Sales kontaktieren
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Right: meta info */}
          <div className="lg:col-span-6 lg:pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <FooterMeta icon={MapPin} label="Hauptsitz">
                Hermannstadt · Sibiu, Rumänien<br />
                Remote-only · DACH & EU
              </FooterMeta>
              <FooterMeta icon={Mail} label="Kontakt">
                <a href="mailto:hello@zertivo.com" className="hover:text-white transition-colors">hello@zertivo.com</a><br />
                <a href="mailto:sales@zertivo.com" className="hover:text-white transition-colors">sales@zertivo.com</a>
              </FooterMeta>
            </div>
            <div className="mt-10 pt-10 border-t border-white/10 grid grid-cols-3 gap-6">
              <FooterStat value="247" label="Geprüfte Unternehmen" />
              <FooterStat value="1.840" label="Audits" />
              <FooterStat value="98 %" label="Zufriedenheit" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── LINK GRID ─── */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-inter text-[10.5px] tracking-[0.28em] uppercase font-semibold text-[#00D387] mb-5">
                {col.title}
              </p>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-[13.5px] font-light text-white/70 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ─── BASE BAR ─── */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-7 flex flex-wrap items-center justify-between gap-y-4 gap-x-8 text-[12px] font-light text-white/55">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00D387] animate-pulse" />
              Live · alle Systeme normal
            </span>
            <span className="text-white/25">·</span>
            <span>© {year} Zertivo</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/legal/dsgvo" className="hover:text-white transition-colors">DSGVO</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
            <Link to="/agb" className="hover:text-white transition-colors">AGB</Link>
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/policies" className="hover:text-white transition-colors">Richtlinien</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterMeta({ icon: Icon, label, children }) {
  return (
    <div>
      <p className="inline-flex items-center gap-2 font-inter text-[10.5px] tracking-[0.28em] uppercase font-semibold text-[#00D387] mb-3">
        <Icon className="w-3 h-3" />
        {label}
      </p>
      <div className="text-[13px] font-light text-white/70 leading-[1.7]">{children}</div>
    </div>
  );
}

function FooterStat({ value, label }) {
  return (
    <div>
      <p className="font-manrope text-[28px] sm:text-[36px] font-extralight tracking-[-0.03em] tabular-nums text-white">
        {value}
      </p>
      <p className="mt-1 text-[10.5px] tracking-[0.16em] uppercase font-light text-white/45">{label}</p>
    </div>
  );
}
